import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, ButtonBase, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, Link, Skeleton, Stack, Switch, TextField, Typography, alpha, useTheme,
} from '@mui/material';
import { CheckCircle, PrintOutlined, CreditCard, GridViewOutlined, VisibilityOutlined } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import { idCardService } from '../../services/idCardService';
import type { IdCardAudience, IdCardDesign } from '../../types/idCards';
import { fetchPhotoDataUrls, fmtDate, shrinkForPrint, usePdfExport } from '../../utils/pdf';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import { AUDIENCE_LABEL, layoutOf } from './catalog';
import { samplePerson, type CardPerson } from './cardPeople';
import { useCardContext } from './useCardContext';
import CardSidesPreview from './CardSidesPreview';
import IdCardsDocument, { type CardPaper } from './render/IdCardsDocument';

/** The most cards one print run makes — a PDF beyond this is unwieldy to send to a printer. */
export const MAX_PRINT = 1000;

/** Image sizes for print, in px on the longer side. A card photo prints about
 *  2 cm tall, the masthead logo under 1 cm and the watermark about 3 cm —
 *  these stay sharper than 300 dpi. A PDF embeds every copy of an image, so
 *  each one is shrunk before it goes in. */
const PRINT_PHOTO_PX = 480;
const PRINT_LOGO_PX = 160;
const PRINT_WATERMARK_PX = 360;

interface Props {
  audience: IdCardAudience;
  /** Who to print for; resolved when the dialog opens. */
  loadPeople: () => Promise<CardPerson[]>;
  /** "Class 6 · B" or "3 selected" — shown in the title. */
  scope: string;
  onClose: () => void;
}

const Choice: React.FC<{ selected: boolean; onClick: () => void; icon: React.ReactNode; title: string; hint: string }> = ({ selected, onClick, icon, title, hint }) => {
  const theme = useTheme();
  return (
    <ButtonBase
      onClick={onClick}
      aria-pressed={selected}
      sx={{
        flex: 1, display: 'flex', alignItems: 'flex-start', gap: 1.25, p: 1.5, textAlign: 'left', borderRadius: 2, border: 1.5,
        borderColor: selected ? 'primary.main' : 'divider',
        bgcolor: selected ? alpha(theme.palette.primary.main, 0.05) : 'transparent',
        transition: 'border-color .15s, background-color .15s',
      }}
    >
      <Box sx={{ color: selected ? 'primary.main' : 'text.secondary', mt: 0.25 }}>{icon}</Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" fontWeight={600}>{title}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.35 }}>{hint}</Typography>
      </Box>
      {selected && <CheckCircle color="primary" sx={{ fontSize: 18 }} />}
    </ButtonBase>
  );
};

const Label: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 700 }}>{children}</Typography>
);

/**
 * Print ID cards: choose the design, the paper and the validity date, see
 * the first card, then download the PDF. Nothing is saved — cards print
 * from each person's current record.
 */
const PrintCardsDialog: React.FC<Props> = ({ audience, loadPeople, scope, onClose }) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { canRead } = useModulePermissions();
  const { school, ready: schoolReady, defaultValidUntil } = useCardContext();
  const pdf = usePdfExport();

  const [people, setPeople] = useState<CardPerson[] | null>(null);
  const [designs, setDesigns] = useState<IdCardDesign[] | null>(null);
  const [designId, setDesignId] = useState<string>('');
  const [paper, setPaper] = useState<CardPaper>('a4');
  const [validUntil, setValidUntil] = useState('');
  const [showValidity, setShowValidity] = useState(true);
  const [skipNoPhoto, setSkipNoPhoto] = useState(false);
  const [previewPhoto, setPreviewPhoto] = useState<string | null>(null);

  // Who and which designs are read once, when the dialog opens.
  const opening = useRef({ loadPeople, onClose });
  useEffect(() => {
    let cancelled = false;
    const { loadPeople: load, onClose: close } = opening.current;
    Promise.all([load(), idCardService.listDesigns(audience, { limit: 50 })])
      .then(([list, res]) => {
        if (cancelled) return;
        setPeople(list.slice(0, MAX_PRINT));
        setDesigns(res.items);
        setDesignId(res.items.find(d => d.isDefault)?._id ?? res.items[0]?._id ?? '');
      })
      .catch((err: any) => {
        if (cancelled) return;
        enqueueSnackbar(err?.message || 'Could not prepare the cards', { variant: 'error' });
        close();
      });
    return () => { cancelled = true; };
  }, [audience, enqueueSnackbar]);

  useEffect(() => { if (!validUntil && schoolReady) setValidUntil(defaultValidUntil(audience)); }, [schoolReady, validUntil, defaultValidUntil, audience]);

  const design = designs?.find(d => d._id === designId) ?? null;
  const missingPhotos = audience === 'student' ? (people ?? []).filter(p => !p.photoFilename).length : 0;
  const printing = useMemo(
    () => (people ?? []).filter(p => !(skipNoPhoto && audience === 'student' && !p.photoFilename)),
    [people, skipNoPhoto, audience],
  );
  const first = printing[0] ?? null;

  useEffect(() => {
    let cancelled = false;
    setPreviewPhoto(null);
    if (first?.photoFilename) {
      fetchPhotoDataUrls([first.photoFilename]).then(p => { if (!cancelled) setPreviewPhoto(p[first.photoFilename!] ?? null); });
    }
    return () => { cancelled = true; };
  }, [first]);

  const validLabel = showValidity && validUntil ? fmtDate(validUntil) : null;
  const total = printing.length;
  const perSheet = design ? (layoutOf(design.layout).orientation === 'portrait' ? 9 : 10) : 10;
  const pages = design ? (paper === 'a4' ? Math.ceil(total / perSheet) : total) * (design.back.enabled ? 2 : 1) : 0;
  const truncated = (people?.length ?? 0) >= MAX_PRINT;

  const documentFor = async () => {
    const [photos, logo, watermark] = await Promise.all([
      fetchPhotoDataUrls(printing.map(p => p.photoFilename), { maxPx: PRINT_PHOTO_PX }),
      // An image the browser can't redraw prints as it is.
      school!.logo ? shrinkForPrint(school!.logo, PRINT_LOGO_PX).then(l => l ?? school!.logo) : null,
      school!.logo ? shrinkForPrint(school!.logo, PRINT_WATERMARK_PX).then(l => l ?? school!.logo) : null,
    ]);
    return (
      <IdCardsDocument
        design={{ ...design!, audience }}
        people={printing}
        photos={photos}
        school={{ ...school!, logo, watermark }}
        validUntil={validLabel}
        paper={paper}
        title={`${AUDIENCE_LABEL[audience]} ID cards — ${scope}`}
      />
    );
  };
  const filename = `${audience}-id-cards-${scope.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}.pdf`;
  const canPrint = !!design && !!school && total > 0 && !pdf.loading;

  return (
    <Dialog open onClose={pdf.loading ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        <Typography variant="h6" fontWeight={700} component="div">Print {AUDIENCE_LABEL[audience].toLowerCase()} ID cards</Typography>
        <Typography variant="body2" color="text.secondary">{scope}</Typography>
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, pt: '16px !important' }}>
        {/* Options */}
        <Stack spacing={3} sx={{ width: { md: 380 }, flexShrink: 0 }}>
          <Box>
            <Label>Design</Label>
            {!designs ? <Skeleton variant="rounded" height={56} /> : designs.length === 0 ? (
              <Alert severity="info" variant="outlined">
                There is no {AUDIENCE_LABEL[audience].toLowerCase()} card design yet.{' '}
                {canRead('id_card_designs') && <Link component={RouterLink} to={`/app/id-cards/designs?for=${audience}`}>Create one</Link>}
              </Alert>
            ) : (
              <Stack spacing={1}>
                {designs.map(d => (
                  <Choice
                    key={d._id}
                    selected={d._id === designId}
                    onClick={() => setDesignId(d._id)}
                    icon={<Box sx={{ width: 18, height: 18, borderRadius: '50%', background: `linear-gradient(135deg, ${d.colors.primary} 0 58%, ${d.colors.accent} 58%)` }} />}
                    title={d.isDefault ? `${d.name} · Default` : d.name}
                    hint={`${layoutOf(d.layout).label} · ${d.back.enabled ? 'two-sided' : 'front only'}`}
                  />
                ))}
              </Stack>
            )}
          </Box>

          <Box>
            <Label>Paper</Label>
            <Stack direction="row" spacing={1}>
              <Choice selected={paper === 'a4'} onClick={() => setPaper('a4')} icon={<GridViewOutlined fontSize="small" />}
                title="A4 sheet" hint={`${perSheet} cards a sheet, with crop marks. Backs line up for double-sided printing.`} />
              <Choice selected={paper === 'card'} onClick={() => setPaper('card')} icon={<CreditCard fontSize="small" />}
                title="Card printer" hint="One card a page, CR80 size, for PVC card printers." />
            </Stack>
          </Box>

          <Box>
            <Label>Validity</Label>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <TextField type="date" size="small" label="Valid until" value={validUntil} onChange={e => setValidUntil(e.target.value)}
                disabled={!showValidity} InputLabelProps={{ shrink: true }} sx={{ width: 180 }} />
              <FormControlLabel control={<Switch size="small" checked={showValidity} onChange={e => setShowValidity(e.target.checked)} />} label={<Typography variant="body2">Show on card</Typography>} />
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
              {audience === 'student' ? 'Starts at the end of the active session.' : 'Starts a year from today.'}
            </Typography>
          </Box>

          {missingPhotos > 0 && (
            <Alert severity="warning" variant="outlined" sx={{ alignItems: 'flex-start' }}>
              <Typography variant="body2" fontWeight={600}>
                {missingPhotos} of {people!.length} {people!.length === 1 ? 'student' : 'students'} {missingPhotos === 1 ? 'has' : 'have'} no photo
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Their cards show initials in place of a photo.
              </Typography>
              <FormControlLabel control={<Switch size="small" checked={skipNoPhoto} onChange={e => setSkipNoPhoto(e.target.checked)} />}
                label={<Typography variant="body2">Leave them out of this print</Typography>} />
            </Alert>
          )}
          {truncated && (
            <Alert severity="info" variant="outlined">Only the first {MAX_PRINT} cards are printed in one go — print the rest in a second run.</Alert>
          )}
        </Stack>

        {/* Preview */}
        <Box
          sx={{
            flex: 1, minWidth: 0, minHeight: 420, borderRadius: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 3,
            bgcolor: alpha(theme.palette.text.primary, 0.03),
            backgroundImage: `radial-gradient(${alpha(theme.palette.text.primary, 0.08)} 1px, transparent 1px)`,
            backgroundSize: '16px 16px',
          }}
        >
          {!people || !designs || !school ? <CircularProgress size={28} /> : design ? (
            <>
              <CardSidesPreview width={330}
                design={{ ...design, audience }} person={first ?? samplePerson(audience)} photo={previewPhoto} school={school} validUntil={validLabel} />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 2.5 }}>
                {first ? `First card: ${first.name}` : 'Nobody to print'}
              </Typography>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">Choose a design to see the card.</Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          {people && design ? `${total} ${total === 1 ? 'card' : 'cards'} · ${pages} ${pages === 1 ? 'page' : 'pages'}` : ''}
        </Typography>
        <Button onClick={onClose} disabled={pdf.loading} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button startIcon={<VisibilityOutlined />} disabled={!canPrint} onClick={() => pdf.preview(documentFor)} sx={{ textTransform: 'none' }}>
          Open PDF
        </Button>
        <Button variant="contained" disableElevation startIcon={pdf.loading ? <CircularProgress size={16} color="inherit" /> : <PrintOutlined />}
          disabled={!canPrint} onClick={() => pdf.generate(documentFor, filename)} sx={{ textTransform: 'none', px: 2.5 }}>
          {pdf.loading ? 'Preparing…' : `Download ${total} ${total === 1 ? 'card' : 'cards'}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PrintCardsDialog;
